import { createRouter, createWebHistory, type RouteLocationNormalized, type RouteLocationRaw, type NavigationGuardNext } from 'vue-router'
import HomeView from '../views/HomeView.vue'
import { useUsersStore } from '@/stores/users'
import { useTenantsStore } from '@/stores/tenants'
import { useProjectsStore } from '@/stores/projects'
import { getCurrentUser } from 'vuefire'
import { useAssessmentsStore } from '@/stores/assessments'
import { useDatasourcesStore } from '@/stores/datasources'
import { useRegulatoryMonitoringStore } from '@/stores/regulatoryMonitoring'
import { useMaintenanceStore } from '@/stores/maintenance'
import { useAnalyticsStore } from '@/stores/analytics'
import { useAppStore } from '@/stores/app'
import { trackAction, type ProjectActivityMeta } from '@/composables/useActivityTracker'
import { CsmAction } from '@/constants/csmActions'
import { requiresConsent } from '@/utils/terms'
import { invitationLinkQuery } from '@/utils/invitedEmail'

/**
 * Routes that map to a curated CSM action (MYAC-717). Keyed by route `name`.
 * `projectParam` (when set) names the route param holding the project id, so the
 * action is recorded under that project; omit it for tenant-scoped actions.
 * `metaProvider` (optional) returns a state-snapshot label (e.g. norm_count) to ride
 * on the view event — read from live store state at fire time (see spec §3a).
 */
export const ROUTE_ACTIONS: Record<
  string,
  { action: CsmAction; projectParam?: string; metaProvider?: () => ProjectActivityMeta }
> = {
  // tenant-scoped
  dashboard: { action: CsmAction.DashboardViewed },
  // MYAC-1138: dead by construction — `settings` (route above) always carries
  // a `redirect: '/settings/billing'` (below), and vue-router resolves a
  // static redirect BEFORE the guard pipeline (and therefore `afterEach`,
  // which is what reads this map) ever sees a `to`. `to.name` here is always
  // `settings-billing`, never `settings`, so this entry can never fire. Kept
  // (not removed, not re-pointed) because it is a live enum name with
  // historical counts — see `routeActions.test.ts`'s
  // `'settings redirect vs settings_opened'` spec, which proves this and
  // fails loudly if the redirect is ever removed and this comes alive.
  settings: { action: CsmAction.SettingsOpened },
  'settings-billing': { action: CsmAction.PlanPageViewed },
  // project-scoped (param `pid`)
  'qms-project': { action: CsmAction.ProjectOpened, projectParam: 'pid' },
  'products-project': { action: CsmAction.ProjectOpened, projectParam: 'pid' },
  // MYAC-1138: the third project root. qms-project and products-project have always
  // fired this; regulatory-monitoring-project was simply missed.
  'regulatory-monitoring-project': { action: CsmAction.ProjectOpened, projectParam: 'pid' },
  // The list pages are a destination in their own right — their detail routes were
  // wired, their hubs were not.
  'qms-assessments': { action: CsmAction.AssessmentViewed, projectParam: 'pid' },
  'qms-internal-audits': { action: CsmAction.AuditViewed, projectParam: 'pid' },
  'qms-reports': { action: CsmAction.ReportViewed, projectParam: 'pid' },
  'products-reports': { action: CsmAction.ReportViewed, projectParam: 'pid' },
  'products-section': { action: CsmAction.TdModuleOpened, projectParam: 'pid' },
  // MYAC-1514 — a report now opens on its own page, below the list of reports:
  // the event fires there, once per report opened (not on the list too).
  'regulatory-monitoring-report-detail': { action: CsmAction.RegulationReportViewed, projectParam: 'pid', metaProvider: () => ({ norm_count: useRegulatoryMonitoringStore().regulations.length }) },
  // Tier 2 — project-scoped view routes
  'qms-documentation': { action: CsmAction.ProjectDocumentationViewed, projectParam: 'pid' },
  'products-documentation': { action: CsmAction.ProjectDocumentationViewed, projectParam: 'pid' },
  'qms-assessments-aid': { action: CsmAction.AssessmentViewed, projectParam: 'pid' },
  'products-assessments': { action: CsmAction.AssessmentViewed, projectParam: 'pid' }, // TD: latest-assessment view (not a list)
  'products-assessments-aid': { action: CsmAction.AssessmentViewed, projectParam: 'pid' },
  'qms-internal-audits-iaid': { action: CsmAction.AuditViewed, projectParam: 'pid' },
  'qms-tasks': { action: CsmAction.TaskViewed, projectParam: 'pid' },
  'products-tasks': { action: CsmAction.TaskViewed, projectParam: 'pid' },
  'qms-task': { action: CsmAction.TaskViewed, projectParam: 'pid' },
  'products-task': { action: CsmAction.TaskViewed, projectParam: 'pid' },
  'qms-chat': { action: CsmAction.ChatOpened, projectParam: 'pid' },
  'products-chat': { action: CsmAction.ChatOpened, projectParam: 'pid' },
  'regulatory-monitoring-norms': { action: CsmAction.RegulationNormsViewed, projectParam: 'pid', metaProvider: () => ({ norm_count: useRegulatoryMonitoringStore().regulations.length }) },
  // MYAC-1138 — project-scoped destinations
  'qms-referentials': { action: CsmAction.ReferentialsViewed, projectParam: 'pid' },
  'products-referentials': { action: CsmAction.ReferentialsViewed, projectParam: 'pid' },
  // MYAC-1522 — every Pilotage section is a visit of the Pilotage (one child route each).
  ...Object.fromEntries(
    PILOTAGE_ROUTE_NAMES.map((name) => [name, { action: CsmAction.ReferentialsViewed, projectParam: 'pid' }]),
  ),
  'qms-registers': { action: CsmAction.RegisterViewed, projectParam: 'pid' },
  'products-change-control': { action: CsmAction.ChangeAnalysisViewed, projectParam: 'pid' },
  'products-risk-analysis': { action: CsmAction.RiskAnalysisViewed, projectParam: 'pid' },
  // MYAC-776 — both shells' list pages are destinations in their own right.
  // The DETAIL routes are deliberately not wired: they would double-count the
  // list visit that always precedes them (the map's own rule at :138).
  'products-gap-analysis': { action: CsmAction.GapAnalysisViewed, projectParam: 'pid' },
  'qms-gap-analysis': { action: CsmAction.GapAnalysisViewed, projectParam: 'pid' },
  'qms-directives': { action: CsmAction.DirectiveViewed, projectParam: 'pid' },
  'products-directives': { action: CsmAction.DirectiveViewed, projectParam: 'pid' },
  'qms-settings': { action: CsmAction.ProjectSettingsViewed, projectParam: 'pid' },
  'products-settings': { action: CsmAction.ProjectSettingsViewed, projectParam: 'pid' },
  // Shares ProjectSettingsViewed rather than its own member — a project's settings
  // page like the other two, not a distinct destination.
  'regulatory-monitoring-settings': { action: CsmAction.ProjectSettingsViewed, projectParam: 'pid' },
  'regulatory-monitoring-profile': { action: CsmAction.RegulationProfileViewed, projectParam: 'pid' },
  'regulatory-monitoring-suggestions': { action: CsmAction.RegulationSuggestionsViewed, projectParam: 'pid' },
  // MYAC-1138 — tenant-scoped destinations
  'qms-list': { action: CsmAction.ProjectListViewed },
  'products-list': { action: CsmAction.ProjectListViewed },
  'regulatory-monitoring-list': { action: CsmAction.ProjectListViewed },
  'settings-users': { action: CsmAction.TeamViewed },
  'settings-company': { action: CsmAction.CompanyProfileViewed },
  'settings-projects': { action: CsmAction.ProjectAdminViewed },
  'profile-info': { action: CsmAction.AccountProfileViewed },
  'profile-notifications': { action: CsmAction.AccountProfileViewed },
  'setup-resubscribe': { action: CsmAction.BillingResubscribePromptViewed },
}
import { useFeatureFlagsStore } from '@/stores/featureFlags'
import { useTasksStore } from '@/stores/tasks'
import { logger } from '@/utils/logger'
import { createChatPageMovedGuard, createFeatureFlagGuard, ensureProjectInCurrentTenant } from '@/utils/router-guards'
import { onRouterError } from './chunkReload'
import { PILOTAGE_ROUTE_NAMES, pilotageTabGuard } from '@/utils/pilotageRoutes'
import { installPilotageSectionFilters } from './pilotageSectionFilters'
import { pilotageRecords } from './pilotageRecords'
import { accessErrorRouteName } from '@/utils/access-errors'
import { projectRouteBase } from '@/models/Project'
import { projectHome, type ProjectTrack } from '@/utils/projectHome'
import { hasTaskDeepLink } from '@/composables/useTaskDeepLinkFocus'
import { isArrival, isFreshSignIn, shouldShowWelcomeLetter } from '@/utils/welcomeLetter'

function projectTypeRedirect(to: RouteLocationNormalized): RouteLocationRaw | undefined {
  const projectsStore = useProjectsStore()
  const project = projectsStore.currentProject
  if (!project?.type) return undefined
  const correctBase = projectRouteBase(project.type)
  const pid = to.params.pid as string
  if (to.path.startsWith(`${correctBase}/${pid}`)) return undefined
  const subPath = to.path.replace(/^\/(qms|products|regulatory-monitoring)\/[^/]+/, '')
  // Only the base was wrong. The query and hash are what a deep link came for:
  // a task's "Open proposal" link is always a QMS address, and on a product it
  // lost its `?task=` here and opened the default tab (MYAC-1285). Returned as
  // one location so no caller can pass the path on and drop the rest again.
  return { path: `${correctBase}/${pid}${subPath || ''}`, query: to.query, hash: to.hash }
}

/**
 * Where this project's user should land (IR-116 §5). Used by the two project
 * ROOT routes and by every feature-flag-off fallback that used to hard-code the
 * legacy assessments route (review N1).
 */
function resolveProjectHome(to: RouteLocationNormalized, track: ProjectTrack): RouteLocationRaw {
  return projectHome(to.params.pid as string, track)
}

/** The flag-off fallback resolver, in the shape `createFeatureFlagGuard` takes. */
function projectHomeFallback(track: ProjectTrack) {
  return (to: RouteLocationNormalized): RouteLocationRaw => resolveProjectHome(to, track)
}

const router = createRouter({
  // @ts-ignore TS1343: 'import.meta' is valid in Vite ESM builds
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    if (to.hash) {
      // In-page anchors: smooth-scroll to the target element.
      return { el: to.hash, behavior: 'smooth' }
    }
    if (to.matched.some(r => r.name === 'regulatory-monitoring-project')) {
      return { top: 0 }
    }
    return false
  },
  routes: [
    // Maintenance route (public)
    {
      path: '/maintenance',
      name: 'maintenance',
      component: () => import('@/views/MaintenanceView.vue'),
      // No requiresAuth meta, route is public
    },
    // Debug route (public, gated by query inside component)
    {
      path: '/debug/env',
      name: 'debug-env',
      component: () => import('@/views/debug/EnvDebugView.vue'),
    },
    // Chat history sandbox (MYAC-918) — UNLISTED: no nav references this
    // route; the only way in is the URL. Reads views/chat_sessions (member-
    // gated by rules) and talks to the sandbox ring's V agent. Ships in every
    // build on purpose (unlike the /dev/* previews) so it is reachable on the
    // hosted dev environment.
    {
      path: '/sandbox/chat/:pid?',
      name: 'sandbox-chat',
      meta: { requiresAuth: true },
      component: () => import('@/views/sandbox/ChatSandboxView.vue'),
    },
    // The gap-analysis status/badge board (MYAC-776 R4 / D19) — a design
    // reference, not a product page: it exists only in a dev build, so the
    // route is spread in conditionally and no built bundle carries it.
    ...(import.meta.env.DEV
      ? [{
          path: '/dev/gap-status-preview',
          name: 'dev-gap-status-preview',
          component: () => import('@/views/dev/GapStatusPreviewView.vue'),
        }, {
          // MYAC-1162 — the authored-document reader with no data behind it. The
          // states that matter (failed read, empty read, an unknown lifecycle
          // value) are the ones dev has none of.
          path: '/dev/authored-document-preview',
          name: 'dev-authored-document-preview',
          component: () => import('@/views/dev/AuthoredDocumentPreviewView.vue'),
        }, {
          // MYAC-1228 — the smart rerun card in every state, with example data: a proposal
          // needs an edited cited document, which dev holds on no project on demand.
          path: '/dev/smart-rerun-preview',
          name: 'dev-smart-rerun-preview',
          component: () => import('@/views/dev/SmartRerunPreviewView.vue'),
        }, {
          // MYAC-1496 lot 1 — the v3 finalisation dialog with a long watch list,
          // in every state: a QA project holds a handful of lines.
          path: '/dev/finalise-dialog-preview',
          name: 'dev-finalise-dialog-preview',
          component: () => import('@/views/dev/FinaliseDialogPreviewView.vue'),
        }]
      : []),
    // Public legal routes → rendered in-app from the bundled markdown
    {
      path: '/terms',
      name: 'terms',
      component: () => import('../views/LegalDocumentView.vue'),
      meta: { legalDoc: 'terms' },
      alias: ['/cgu', '/gcu', '/terms-and-conditions']
    },
    {
      path: '/privacy',
      name: 'privacy',
      component: () => import('../views/LegalDocumentView.vue'),
      meta: { legalDoc: 'privacy' },
      alias: ['/privacy-policy']
    },
    {
      path: '',
      component: HomeView,
      redirect: '/dashboard',
      meta: {
        requiresAuth: true,
        guard: async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {

          logger.debug('Home guard')
          const tenantsStore = useTenantsStore()
          const projectsStore = useProjectsStore()
          const usersStore = useUsersStore()
          const initTasks = [
            tenantsStore.listTenants(false),
            projectsStore.listProjects(false),
            usersStore.loadInvitationsForUser(),
          ]
          return Promise.all(initTasks).then(() => {
            // If user has no tenant, drive them to the setup choice page
            if (tenantsStore.tenants?.length === 0) {
              // Dedicated entry (/register?plan=veille): a preselected plan means
              // "create a company on this plan" → go straight to setup, skipping the
              // choice screen entirely (no 1s flash from a component-level redirect).
              if (localStorage.getItem('setup.preselectedPlan')) {
                return next({ name: 'setup' })
              }
              if (usersStore.invitations?.length > 0) {
                // MYAC-1522 — the invitation link travels on, so the invitations
                // page can accept the one the person came for without a second
                // click.
                const invitation = invitationLinkQuery(to.query)
                return next(invitation ? { name: 'setup-invitations', query: invitation } : { name: 'setup-invitations' })
              }
              else {
                return next({ name: 'setup-choice' })
              }
            }
            // Multi-tenant user without a valid persisted choice → ask them to
            // pick. listTenants leaves currentTenantId empty in this case.
            if ((tenantsStore.tenants?.length || 0) > 1 && !tenantsStore.currentTenantId) {
              return next({
                name: 'choose-tenant',
                query: to.fullPath !== '/' ? { redirect: to.fullPath } : undefined,
              })
            }
            // no subscription
            if (!tenantsStore.currentTenant?.subscription_data) {
              return next({
                name: 'setup'
              })
            }
            // subscription canceled
            if (tenantsStore.currentTenant?.subscription_data?.status === 'canceled') {
              return next({
                name: 'setup-resubscribe'
              })
            }
            // MYAC-1522 — the welcome letter greets an arrival on the dashboard: at
            // sign-in, and at the first visit of the day. Decided here, once the
            // company is known, so the letter can tell a new company from one at work.
            if (to.name === 'dashboard' && isArrival(from)
              && shouldShowWelcomeLetter(usersStore.user?.uid ?? '', { freshSignIn: isFreshSignIn(from) })) {
              return next({ name: 'welcome' })
            }
            // If there are no projects, remain on dashboard; header will auto-open create dialog
            // No redirect to setup-project
            return true
          })
            .catch((error) => {
              // Distinguish a permission denial (→ access-denied page) from a
              // genuine not-found / unknown error (→ 404), rather than blanket
              // 404 for every failure (MYAC-758).
              logger.error('Home guard error', error)
              return next({ name: accessErrorRouteName(error) })
            })
        },
      },
      children: [
        {
          path: '',
          name: 'home',
          redirect: '/dashboard',
        },
        // MYAC-1522 — the welcome letter: full screen, no header (`meta.bare`), the
        // app one click away. Reached from the guard above on an arrival.
        {
          path: '/welcome',
          name: 'welcome',
          meta: { title: 'pageTitles.welcome', bare: true },
          component: () => import('../views/welcome/WelcomeLetterView.vue'),
        },
        // MYAC-1522 — a QMS, product or watch that is not shared with the person,
        // said inside the app's frame (header, chat), reached from
        // `ensureProjectInCurrentTenant` once the home guard above has passed. Not
        // the bare `/denied` below, which stays outside the frame for the cases
        // where the frame itself is what failed (this home guard's own access
        // error, which would loop here) or no company is loaded (signed out).
        {
          path: '/no-access',
          name: 'project-denied',
          component: () => import('../views/errors/DeniedView.vue'),
          props: { inApp: true },
        },
        // Dashboard
        {
          path: '/dashboard',
          name: 'dashboard',
          meta: { title: 'pageTitles.dashboard' },
          component: () => import('../views/dashboard/DashboardPage.vue'),
        },
        // MYAC-1268 — the tenant-wide task list. A sibling of the dashboard,
        // not a child of any project: its whole subject is the work across
        // every project, so it can carry no `pid`.
        {
          path: '/tasks',
          name: 'global-tasks',
          meta: { title: 'pageTitles.global_tasks' },
          component: () => import('../views/tasks/GlobalTasksView.vue'),
        },
        // Settings
        {
          path: '/settings',
          name: 'settings',
          meta: { title: 'pageTitles.settings' },
          component: () => import('@/views/settings/SettingsView.vue'),
          redirect: '/settings/billing',
          children: [
            {
              path: 'billing',
              name: 'settings-billing',
              component: () => import('@/views/settings/billing/BillingView.vue'),
            },
            {
              path: 'users',
              name: 'settings-users',
              component: () => import('@/views/settings/users/UsersView.vue'),
            },
            {
              path: 'projects',
              name: 'settings-projects',
              component: () => import('@/views/settings/projects/ProjectsView.vue'),
            },
            {
              path: 'company',
              name: 'settings-company',
              component: () => import('@/views/settings/company/CompanyView.vue'),
            },
            {
              // MYAC-1515 — the company's connections to its document sources.
              path: 'connections',
              name: 'settings-connections',
              meta: {
                // The menu entry is gated, so the URL is too — otherwise the
                // surface is only hidden, not off.
                guard: createFeatureFlagGuard(
                  () => useFeatureFlagsStore().isTenantConnectionsEnabled,
                  'settings-billing',
                  undefined,
                  'Company connections guard'
                ),
              },
              component: () => import('@/views/settings/connections/ConnectionsView.vue'),
            },
          ],
        },
        // Profile
        {
          path: '/profile',
          name: 'profile',
          meta: { title: 'pageTitles.profile' },
          component: () => import('@/views/profile/ProfileView.vue'),
          redirect: '/profile/info',
          children: [
            {
              path: 'info',
              name: 'profile-info',
              component: () => import('@/views/profile/ProfileInfoView.vue'),
            },
            {
              path: 'notifications',
              name: 'profile-notifications',
              component: () => import('@/views/profile/NotificationsView.vue'),
            },
          ],
        },
        // QMS
        {
          path: '/qms',
          name: 'qms',
          redirect: (to) => `/qms`,
          children: [
            {
              path: '',
              name: 'qms-list',
              meta: { title: 'pageTitles.qms' },
              component: () => import('../views/qms/QMSListView.vue'),
            },
            {
              path: ':pid',
              name: 'qms-project',
              component: () => import('../views/qms/pid/QMSView.vue'),
              meta: {
                // The project shell (menu, score) stays mounted across the project's
                // pages; only its inner page is keyed (HomeView / usePageViewKey).
                projectShell: true,
                guard: async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
                  logger.debug('QMS project guard')
                  if (!(await ensureProjectInCurrentTenant(to, next))) return false
                  const projectsStore = useProjectsStore()
                  projectsStore.setCurrentProject(to.params.pid as string)
                  const redirect = projectTypeRedirect(to)
                  if (redirect) return next(redirect)
                  // IR-116 §5: the per-track legacy existence is decided HERE
                  // (review B4/B5) — one memoized limit(1) read, in parallel with
                  // the datasources the guard already awaits. Every later reader
                  // (the nav, the datasource prompt) is then a cache hit.
                  const datasourcesStore = useDatasourcesStore()
                  await Promise.all([
                    datasourcesStore.listDatasources(to.params.pid as string),
                    useAssessmentsStore().hasLegacyAssessments(to.params.pid as string, 'qms'),
                  ])
                  return true
                },
              },
              children: [
                {
                  path: '',
                  name: 'qms-project-root',
                  // IR-116 §5: lands on the home `resolveProjectHome` names, the
                  // same resolver the flag-off fallbacks use.
                  component: () => import('../views/ProjectHomeRedirect.vue'),
                  meta: {
                    guard: async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
                      logger.debug('QMS project root guard')
                      return next(resolveProjectHome(to, 'qms'))
                    },
                  },
                },
                // MYAC-1522 — the Pilotage: the legacy `referentials` redirect, then ONE
                // layout record with one child route per section (/conversation,
                // /essentials, …), generated from a table (router/pilotageRecords.ts).
                ...pilotageRecords('qms'),
                {
                  path: 'documents',
                  name: 'qms-documents',
                  meta: { title: 'pageTitles.documents' },
                  component: () => import('../views/qms/pid/documents/QMSAuthoredDocumentsView.vue'),
                },
                {
                  // MYAC-1161 v1: the page a chat-written document's link opens.
                  // The chat's create card and the side panel are still MYAC-1162.
                  path: 'documents/:docId',
                  name: 'qms-document',
                  meta: { title: 'pageTitles.document' },
                  component: () => import('../views/qms/pid/documents/QMSAuthoredDocumentView.vue'),
                },
                {
                  path: 'documentation',
                  name: 'qms-documentation',
                  component: () => import('../views/qms/pid/documentation/QMSDocumentationListView.vue'),
                },
                {
                  // MYAC-776 — D14: the engine is project-kind-agnostic, so the
                  // QMS shell gets the same module. The three TD-only pieces
                  // (topic index, the change-analysis handoff, the TD menu) are
                  // named in spec §1.3; only the handoff is visible here, and
                  // `kind` is what switches it off.
                  path: 'gap-analysis',
                  name: 'qms-gap-analysis',
                  meta: {
                    title: 'pageTitles.gap_analysis',
                  },
                  component: () => import('../views/gap-analysis/GapRegisterView.vue'),
                  props: { kind: 'qms' },
                },
                {
                  path: 'gap-analysis/:gapId',
                  name: 'qms-gap-analysis-detail',
                  meta: {
                    title: 'pageTitles.gap_analysis',
                    ownsQuery: true,
                  },
                  component: () => import('../views/gap-analysis/GapDetailView.vue'),
                  props: { kind: 'qms' },
                },
                {
                  // MYAC-1013 — the four registers (NC, CAPA, Suppliers/Customers,
                  // Change Control) under ONE tab. The three routes they used to
                  // live at are kept below as redirects into `?tab=`.
                  path: 'registers',
                  name: 'qms-registers',
                  meta: {
                    title: 'pageTitles.registers',
                    // The view drives ?tab= itself — don't remount on query change,
                    // or every register subscription is torn down on each tab click.
                    ownsQuery: true,
                    guard: createFeatureFlagGuard(
                      () => {
                        const flags = useFeatureFlagsStore()
                        // Either flag alone still leaves a usable tab — only a project
                        // with NEITHER has no registers to show.
                        return flags.isQmsNcCapaEnabled || flags.isQmsSuppliersCustomersEnabled
                      },
                      projectHomeFallback('qms'),
                      undefined,
                      'QMS registers guard'
                    ),
                  },
                  component: () => import('../views/qms/pid/registers/QMSRegistersView.vue'),
                },
                {
                  // Legacy URLs (bookmarks, shared links, the chat's own links) —
                  // the registers converged into one tab; the route NAMES stay as
                  // stable ids pointing at their tab.
                  path: 'capa-list',
                  name: 'qms-capa-list',
                  redirect: (to) => ({
                    name: 'qms-registers',
                    params: { pid: to.params.pid as string },
                    query: { tab: 'nc' },
                  }),
                },
                {
                  path: 'change-control-list',
                  name: 'qms-change-control-list',
                  redirect: (to) => ({
                    name: 'qms-registers',
                    params: { pid: to.params.pid as string },
                    query: { tab: 'change_control' },
                  }),
                },
                {
                  path: 'internal-audit',
                  name: 'qms-internal-audits',
                  meta: {
                    title: 'pageTitles.internal_audits',
                  },
                  component: () => import('../views/qms/pid/internal-audits/QMSInternalAuditsView.vue'),
                },
                {
                  path: 'supplier-customer-management',
                  name: 'qms-supplier-customer-management',
                  redirect: (to) => ({
                    name: 'qms-registers',
                    params: { pid: to.params.pid as string },
                    query: { tab: 'supplier' },
                  }),
                },
                {
                  path: 'supplier-customer-management/:prid',
                  name: 'qms-supplier-customer-partner',
                  meta: {
                    title: 'pageTitles.supplier_customer_management',
                    guard: createFeatureFlagGuard(
                      () => useFeatureFlagsStore().isQmsSuppliersCustomersEnabled,
                      projectHomeFallback('qms'),
                      undefined,
                      'QMS supplier-customer partner guard'
                    ),
                  },
                  component: () => import('../views/qms/pid/supplier-customer-management/prid/QMSSupplierCustomerPartnerView.vue'),
                },

                {
                  path: 'assessments',
                  name: 'qms-assessments',
                  component: () => import('../views/qms/pid/assessments/QMSAssessmentsView.vue'),
                  meta: {
                    guard: async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
                      logger.debug('QMS assessments guard')
                      const assessmentsStore = useAssessmentsStore()
                      await assessmentsStore.listAssessments(to.params.pid as string)
                      return true
                    },
                  },
                },
                {
                  path: 'assessments/:aid',
                  name: 'qms-assessments-aid',
                  component: () => import('../views/qms/pid/assessments/aid/QMSAssessmentView.vue'),
                  meta: {
                    guard: async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
                      logger.debug('QMS assessments aid guard')
                      const assessmentsStore = useAssessmentsStore()
                      await assessmentsStore.getAssessment(to.params.pid as string, to.params.aid as string)
                      return true
                    },
                  },
                },
                {
                  path: 'internal-audit/:iaid',
                  name: 'qms-internal-audits-iaid',
                  meta: {
                    title: 'pageTitles.internal_audit',
                  },
                  component: () => import('@/views/qms/pid/internal-audits/aid/QMSInternalAuditView.vue'),
                },
                {
                  path: 'tasks',
                  name: 'qms-tasks',
                  meta: {
                    // MYAC-1264 — `?source=`/`?gap=`/`?task=` are the board's own
                    // state. Without this a query change remounts the page, and the
                    // old instance's unmount unsubscribes the store AFTER this guard
                    // skipped reloading the same project: an empty board, for good.
                    ownsQuery: true,
                    guard: async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
                      const pid = to.params.pid as string
                      if (pid) {
                        const tasksStore = useTasksStore()
                        tasksStore.setCurrentUserUid(
                          (await import('@/stores/users')).useUsersStore().uid ?? '',
                        )
                        await tasksStore.loadKanbanTasks(pid)
                      }
                      return true
                    },
                  },
                  component: () => import('../views/qms/pid/QMSTasksView.vue'),
                },
                {
                  // MYAC-1418 — one task on its own page. The id is in the PATH,
                  // not `?task=`: a path survives the QMS/Product type redirect.
                  // No guard loads the board here — the page loads it on mount
                  // (see `TaskPageView` for why a guard would be emptied).
                  path: 'tasks/:taskId',
                  name: 'qms-task',
                  meta: { title: 'pageTitles.task', navEntry: 'qms-tasks' },
                  component: () => import('@/views/tasks/TaskPageView.vue'),
                },
                {
                  path: 'directives',
                  name: 'qms-directives',
                  meta: {
                    // The nav entry is gated, so the URL is too — otherwise the
                    // surface is only hidden, not off.
                    guard: createFeatureFlagGuard(
                      () => useFeatureFlagsStore().isTaskDirectivesEnabled,
                      projectHomeFallback('qms'),
                      undefined,
                      'Task directives guard'
                    ),
                  },
                  component: () => import('../views/qms/pid/QMSDirectivesView.vue'),
                },
                {
                  // MYAC-1051 — the standing inputs the client gave the engine.
                  path: 'playbook',
                  name: 'qms-playbook',
                  meta: {
                    title: 'pageTitles.playbook',
                    // Its own flag (release 2026-09-03): an unflagged company must
                    // not reach the page by URL either — the nav already hides it.
                    guard: createFeatureFlagGuard(
                      () => useFeatureFlagsStore().isPlaybookEnabled,
                      projectHomeFallback('qms'),
                      undefined,
                      'QMS playbook guard'
                    ),
                  },
                  component: () => import('../views/qms/pid/QMSPlaybookView.vue'),
                },
                {
                  path: 'reports',
                  name: 'qms-reports',
                  component: () => import('../views/qms/pid/QMSReportsView.vue'),
                },
                {
                  path: 'chat',
                  name: 'qms-chat',
                  meta: { guard: createChatPageMovedGuard('qms-chat', 'qms') },
                  component: () => import('../views/qms/pid/QMSChatView.vue'),
                },
                // MYAC-1140 — the saved-prompts library, anchored at a real
                // URL rather than behind a modal (Aymeric, 2026-08-28); the chat
                // dock survives navigation (MYAC-917) so opening one costs
                // nothing. MYAC-1522: /prompts, no longer under the old chat page,
                // which only leads to the conversation now.
                {
                  path: 'prompts',
                  name: 'qms-chat-prompts',
                  meta: {
                    title: 'pageTitles.saved_prompts',
                  },
                  component: () => import('../views/chat/SavedPromptsListView.vue'),
                },
                {
                  path: 'prompts/:promptId',
                  name: 'qms-chat-prompt',
                  meta: {
                    title: 'pageTitles.saved_prompts',
                    // MYAC-1140 — `?edit=1` is the page's own state (the
                    // editor is up), so a toggle must re-render it, not
                    // remount the routed view and destroy the draft in it.
                    ownsQuery: true,
                  },
                  component: () => import('../views/chat/SavedPromptDetailView.vue'),
                },
                // MYAC-1522 — the library's old addresses, under the chat page.
                { path: 'chat/prompts', redirect: (to) => ({ name: 'qms-chat-prompts', params: to.params, query: to.query, hash: to.hash }) },
                { path: 'chat/prompts/:promptId', redirect: (to) => ({ name: 'qms-chat-prompt', params: to.params, query: to.query, hash: to.hash }) },
                {
                  path: 'settings',
                  name: 'qms-settings',
                  component: () => import('../views/qms/pid/QMSSettingsView.vue'),
                  children: [
                    {
                      path: 'datasources/:dsid',
                      name: 'qms-settings-datasources-dsid',
                      component: () => import('../views/qms/pid/settings/datasources/dsid/QMSSettingsDatasourceView.vue'),
                      meta: {
                        guard: async (
                          to: RouteLocationNormalized,
                          from: RouteLocationNormalized,
                          next: NavigationGuardNext,
                        ) => {
                          const ds = useDatasourcesStore()
                          ds.setCurrentDatasource(to.params.dsid as string)
                          return true
                        },
                      },
                    },
                  ],
                },
              ]
            },
          ],
        },
        // Products
        {
          path: '/products',
          name: 'products',
          redirect: (to) => `/products`,
          children: [
            {
              path: '',
              name: 'products-list',
              meta: { title: 'pageTitles.products' },
              component: () => import('../views/products/ProductsListView.vue'),
            },
            {
              path: ':pid',
              name: 'products-project',
              component: () => import('../views/products/pid/ProductView.vue'),
              meta: {
                // Same as the QMS shell: kept across the product's pages.
                projectShell: true,
                guard: async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
                  logger.debug('Products project guard')
                  if (!(await ensureProjectInCurrentTenant(to, next))) return false
                  const projectsStore = useProjectsStore()
                  projectsStore.setCurrentProject(to.params.pid as string)
                  const redirect = projectTypeRedirect(to)
                  if (redirect) return next(redirect)
                  // IR-116 §5 — same as the QMS parent guard, on the TD track.
                  const datasourcesStore = useDatasourcesStore()
                  await Promise.all([
                    datasourcesStore.listDatasources(to.params.pid as string),
                    useAssessmentsStore().hasLegacyAssessments(to.params.pid as string, 'td'),
                  ])
                  return true
                },
              },
              children: [
                {
                  path: '',
                  name: 'products-project-root',
                  // IR-116 §5 — see the QMS project root.
                  component: () => import('../views/ProjectHomeRedirect.vue'),
                  meta: {
                    guard: async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
                      logger.debug('Products project root guard')
                      return next(resolveProjectHome(to, 'td'))
                    },
                  },
                },
                {
                  path: 'documents',
                  name: 'products-documents',
                  meta: { title: 'pageTitles.documents' },
                  component: () =>
                    import('../views/products/pid/documents/ProductAuthoredDocumentsView.vue'),
                },
                {
                  // MYAC-1161 v1: the page a chat-written document's link opens.
                  // The chat's create card and the side panel are still MYAC-1162.
                  path: 'documents/:docId',
                  name: 'products-document',
                  meta: { title: 'pageTitles.document' },
                  component: () => import('../views/qms/pid/documents/QMSAuthoredDocumentView.vue'),
                },
                {
                  path: 'documentation',
                  name: 'products-documentation',
                  component: () => import('../views/products/pid/documentation/ProductDocumentationListView.vue'),
                },
                {
                  path: 'change-analysis',
                  name: 'products-change-control',
                  meta: {
                    title: 'pageTitles.change_control',
                  },
                  component: () => import('../views/products/pid/change-control/ChangeRegisterView.vue'),
                },
                {
                  path: 'change-analysis/new',
                  name: 'products-change-control-new',
                  meta: {
                    title: 'pageTitles.change_control',
                  },
                  component: () => import('../views/products/pid/change-control/ChangeDetailView.vue'),
                  props: { mode: 'new' },
                },
                {
                  path: 'change-analysis/:changeId',
                  name: 'products-change-control-detail',
                  meta: {
                    title: 'pageTitles.change_control',
                  },
                  component: () => import('../views/products/pid/change-control/ChangeDetailView.vue'),
                  props: { mode: 'existing' },
                },
                {
                  // Legacy URL (bookmarks, shared links) — the module display name
                  // moved to Change Analysis; the route NAME stays the stable id.
                  path: 'change-control',
                  redirect: (to) => ({
                    name: 'products-change-control',
                    params: { pid: to.params.pid as string },
                  }),
                },
                {
                  path: 'risk-analysis',
                  name: 'products-risk-analysis',
                  meta: {
                    title: 'pageTitles.risk_analysis',
                    // The view drives ?severity= / ?basis= / ?finding_basis=
                    // itself — don't remount on a query change (e2e run 8,
                    // anomaly 3). A fullPath-keyed router-view rebuilds the
                    // view on every filter click: the new instance's
                    // `views.bind(pid)` early-returns (same project), then the
                    // OLD instance's onUnmounted calls `views.unbind(pid)` and
                    // tears every subscription down — leaving "Unknown",
                    // "0 rows" and an empty table until a full reload.
                    ownsQuery: true,
                    guard: createFeatureFlagGuard(
                      () => useFeatureFlagsStore().isRiskAnalysisEnabled,
                      projectHomeFallback('td'),
                      undefined,
                      'Risk analysis guard'
                    ),
                  },
                  component: () => import('../views/products/pid/risk/RiskAnalysisView.vue'),
                },
                {
                  // MYAC-776 — the gap analysis module. Same two components as
                  // the QMS shell (spec §1.3, D14); the shell is a `kind` prop,
                  // because the two screens differ only in which routes they
                  // link to and whether the TD-only handoff renders.
                  path: 'gap-analysis',
                  name: 'products-gap-analysis',
                  meta: {
                    title: 'pageTitles.gap_analysis',
                  },
                  component: () => import('../views/gap-analysis/GapRegisterView.vue'),
                  props: { kind: 'td' },
                },
                {
                  path: 'gap-analysis/:gapId',
                  name: 'products-gap-analysis-detail',
                  meta: {
                    title: 'pageTitles.gap_analysis',
                    // The detail view drives ?tab= itself (By clause / By file) —
                    // don't remount on a query change, or every subscription is
                    // torn down on each tab click.
                    ownsQuery: true,
                  },
                  component: () => import('../views/gap-analysis/GapDetailView.vue'),
                  props: { kind: 'td' },
                },
                // MYAC-1522 — the Pilotage: the legacy `referentials` redirect, then ONE
                // layout record with one child route per section (/conversation,
                // /essentials, …), generated from a table (router/pilotageRecords.ts).
                ...pilotageRecords('products'),

                {
                  path: 'assessments',
                  name: 'products-assessments',
                  component: () => import('../views/products/pid/assessments/ProductAssessmentsView.vue'),
                  meta: {
                    guard: async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
                      logger.debug('Products assessments guard')
                      const assessmentsStore = useAssessmentsStore()
                      await assessmentsStore.listAssessments(to.params.pid as string)
                      return true
                    },
                  },
                },
                {
                  path: 'assessments/:aid',
                  name: 'products-assessments-aid',
                  component: () => import('../views/products/pid/assessments/aid/ProductAssessmentView.vue'),
                  meta: {
                    guard: async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
                      logger.debug('Products assessments aid guard')
                      // MYAC-1525 — the address a TD task's "Open proposal" link
                      // had before it pointed at the TD screen. This page cannot
                      // show a TD proposal, so a link already copied is sent on.
                      if (to.params.aid === 'td_latest' && hasTaskDeepLink(to.query.task)) {
                        return next({ name: 'products-assessments', params: { pid: to.params.pid }, query: to.query, hash: to.hash })
                      }
                      const assessmentsStore = useAssessmentsStore()
                      await assessmentsStore.getAssessment(to.params.pid as string, to.params.aid as string)
                      return true
                    },
                  },
                },
                {
                  path: 'sections/:sectionKey',
                  name: 'products-section',
                  component: () => import('../components/td/TDSectionDetailView.vue'),
                },
                {
                  path: 'chat',
                  name: 'products-chat',
                  meta: { guard: createChatPageMovedGuard('products-chat', 'td') },
                  component: () => import('../views/products/pid/ProductChatView.vue'),
                },
                // MYAC-1140 — the saved-prompts library, anchored at a real
                // URL rather than behind a modal (Aymeric, 2026-08-28); the chat
                // dock survives navigation (MYAC-917) so opening one costs
                // nothing. MYAC-1522: /prompts, no longer under the old chat page,
                // which only leads to the conversation now.
                {
                  path: 'prompts',
                  name: 'products-chat-prompts',
                  meta: {
                    title: 'pageTitles.saved_prompts',
                  },
                  component: () => import('../views/chat/SavedPromptsListView.vue'),
                },
                {
                  path: 'prompts/:promptId',
                  name: 'products-chat-prompt',
                  meta: {
                    title: 'pageTitles.saved_prompts',
                    // MYAC-1140 — `?edit=1` is the page's own state (the
                    // editor is up), so a toggle must re-render it, not
                    // remount the routed view and destroy the draft in it.
                    ownsQuery: true,
                  },
                  component: () => import('../views/chat/SavedPromptDetailView.vue'),
                },
                // MYAC-1522 — the library's old addresses, under the chat page.
                { path: 'chat/prompts', redirect: (to) => ({ name: 'products-chat-prompts', params: to.params, query: to.query, hash: to.hash }) },
                { path: 'chat/prompts/:promptId', redirect: (to) => ({ name: 'products-chat-prompt', params: to.params, query: to.query, hash: to.hash }) },
                {
                  path: 'tasks',
                  name: 'products-tasks',
                  // MYAC-1264 — same board, same reason as `qms-tasks`.
                  meta: { ownsQuery: true },
                  component: () => import('../views/products/pid/ProductTasksView.vue'),
                },
                {
                  // MYAC-1418 — one task on its own page. The id is in the PATH,
                  // not `?task=`: a path survives the QMS/Product type redirect.
                  // No guard loads the board here — the page loads it on mount
                  // (see `TaskPageView` for why a guard would be emptied).
                  path: 'tasks/:taskId',
                  name: 'products-task',
                  meta: { title: 'pageTitles.task', navEntry: 'products-tasks' },
                  component: () => import('@/views/tasks/TaskPageView.vue'),
                },
                {
                  path: 'directives',
                  name: 'products-directives',
                  meta: {
                    // The nav entry is gated, so the URL is too — otherwise the
                    // surface is only hidden, not off.
                    guard: createFeatureFlagGuard(
                      () => useFeatureFlagsStore().isTaskDirectivesEnabled,
                      projectHomeFallback('td'),
                      undefined,
                      'Task directives guard'
                    ),
                  },
                  component: () => import('../views/products/pid/ProductDirectivesView.vue'),
                },
                {
                  // MYAC-1051 — the standing inputs the client gave the engine.
                  path: 'playbook',
                  name: 'products-playbook',
                  meta: {
                    title: 'pageTitles.playbook',
                    // Its own flag (release 2026-09-03): an unflagged company must
                    // not reach the page by URL either — the nav already hides it.
                    guard: createFeatureFlagGuard(
                      () => useFeatureFlagsStore().isPlaybookEnabled,
                      projectHomeFallback('td'),
                      undefined,
                      'Product playbook guard'
                    ),
                  },
                  component: () => import('../views/products/pid/ProductPlaybookView.vue'),
                },
                {
                  path: 'reports',
                  name: 'products-reports',
                  component: () => import('../views/products/pid/ProductReportsView.vue'),
                },
                {
                  path: 'settings',
                  name: 'products-settings',
                  component: () => import('../views/products/pid/ProductSettingsView.vue'),
                  children: [
                    {
                      path: 'datasources/:dsid',
                      name: 'products-settings-datasources-dsid',
                      component: () => import('../views/products/pid/settings/datasources/dsid/ProductSettingsDatasourceView.vue'),
                      meta: {
                        guard: async (
                          to: RouteLocationNormalized,
                          from: RouteLocationNormalized,
                          next: NavigationGuardNext,
                        ) => {
                          const ds = useDatasourcesStore()
                          ds.setCurrentDatasource(to.params.dsid as string)
                          return true
                        },
                      },
                    },
                  ],
                },
              ]
            },
          ],
        },
        // Regulatory monitoring
        {
          path: '/regulatory-monitoring',
          name: 'regulatory-monitoring',
          redirect: () => `/regulatory-monitoring`,
          children: [
            {
              path: '',
              name: 'regulatory-monitoring-list',
              meta: { title: 'pageTitles.regulatory_monitoring' },
              component: () => import('../views/regulatory-monitoring/RegulatoryMonitoringListView.vue'),
            },
            {
              path: ':pid',
              name: 'regulatory-monitoring-project',
              component: () => import('../views/regulatory-monitoring/pid/RegulatoryMonitoringView.vue'),
              meta: {
                guard: async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
                  logger.debug('Regulatory monitoring project guard')
                  if (!(await ensureProjectInCurrentTenant(to, next))) return false
                  const projectsStore = useProjectsStore()
                  projectsStore.setCurrentProject(to.params.pid as string)
                  const redirect = projectTypeRedirect(to)
                  if (redirect) return next(redirect)
                  const datasourcesStore = useDatasourcesStore()
                  await datasourcesStore.listDatasources(to.params.pid as string)
                  const regulatoryMonitoringStore = useRegulatoryMonitoringStore()
                  await regulatoryMonitoringStore.initForProject(to.params.pid as string)
                  return true
                },
              },
              children: [
                // MYAC-1307 — the list is where the client works, so a project
                // opens on it; the report is one level down, a dated proof.
                {
                  path: '',
                  name: 'regulatory-monitoring-project-root',
                  redirect: (to) => `/regulatory-monitoring/${to.params.pid}/norms`,
                },
                {
                  path: 'home',
                  name: 'regulatory-monitoring-home',
                  redirect: (to) => `/regulatory-monitoring/${to.params.pid}/norms`,
                },
                // MYAC-1514 — the reports as a list with where each one stands,
                // and each report on its own page.
                {
                  path: 'report',
                  name: 'regulatory-monitoring-report',
                  component: () =>
                    import('../views/regulatory-monitoring/pid/RegulatoryMonitoringReportsView.vue'),
                },
                {
                  path: 'report/:rid',
                  name: 'regulatory-monitoring-report-detail',
                  // MYAC-1496 rule 9: back from a comparison, `?card=` / `?review=`
                  // are consumed and stripped; without this the strip remounts the page.
                  meta: { ownsQuery: true },
                  component: () =>
                    import('../views/regulatory-monitoring/pid/RegulatoryMonitoringReportView.vue'),
                },
                {
                  path: 'norms',
                  name: 'regulatory-monitoring-norms',
                  // MYAC-1307 — the suggestions used to be a tab of this page,
                  // reached with `?tab=suggested&profile=<id>`. That address is
                  // in bookmarks and in older links: it lands on the page they
                  // became, with the same profile in focus.
                  beforeEnter: (to) =>
                    to.query.tab === 'suggested'
                      ? {
                          name: 'regulatory-monitoring-suggestions',
                          params: { pid: to.params.pid },
                          query: typeof to.query.profile === 'string' ? { profile: to.query.profile } : {},
                        }
                      : true,
                  component: () =>
                    import('../views/regulatory-monitoring/pid/RegulatoryMonitoringNormsView.vue'),
                },
                // MYAC-1320 — one page per watched standard: the projects it
                // applies to and the impact analysis on them. `rowId` is the
                // watched row's own id (`regulation_lists/{rowId}`).
                {
                  path: 'norms/:rowId',
                  name: 'regulatory-monitoring-standard',
                  component: () =>
                    import('../views/regulatory-monitoring/pid/RegulatoryMonitoringStandardView.vue'),
                  props: true,
                },
                {
                  path: 'suggestions',
                  name: 'regulatory-monitoring-suggestions',
                  component: () =>
                    import('../views/regulatory-monitoring/pid/RegulatoryMonitoringSuggestionsView.vue'),
                },
                {
                  path: 'redline',
                  name: 'regulatory-monitoring-redline',
                  meta: {
                    // MYAC-805 — the watchlist arrives here with `?new_redline=1&…`,
                    // and the view consumes those params and strips them. The
                    // router-view is keyed on `fullPath`, so without this the strip
                    // remounts the page and destroys the dialog it just opened —
                    // the user lands on an empty Redline tab (caught live 2026-09-09).
                    ownsQuery: true,
                  },
                  component: () =>
                    import(
                      '../views/regulatory-monitoring/pid/RegulatoryMonitoringRedlineView.vue'
                    ),
                },
                {
                  path: 'settings',
                  name: 'regulatory-monitoring-settings',
                  component: () =>
                    import('../views/regulatory-monitoring/pid/RegulatoryMonitoringSettingsView.vue'),
                },
                {
                  path: 'profiles/:profileId',
                  name: 'regulatory-monitoring-profile',
                  component: () =>
                    import('../views/regulatory-monitoring/pid/RegulatoryMonitoringProfileView.vue'),
                  props: true,
                },
              ],
            },
          ],
        },
      ],
    },

    // Callbacks
    {
      path: '/callbacks',
      name: 'callbacks',
      children: [
        {
          path: 'google_drive_oauth',
          name: 'callbacks-google_drive_oauth',
          meta: { requiresAuth: true },
          component: () => import('../views/callbacks/GoogleDriveOAuthCallbackView.vue'),
        },
        {
          path: 'office365_delegated_oauth',
          name: 'callbacks-office365-delegated-oauth',
          meta: { requiresAuth: true },
          component: () => import('@/views/callbacks/Office365DelegatedOAuthCallbackView.vue'),
        },
        {
          path: 'atlassian_oauth',
          name: 'callbacks-atlassian-oauth',
          meta: { requiresAuth: true },
          component: () => import('@/views/callbacks/AtlassianOAuthCallbackView.vue'),
        },
      ],
    },
    // Auth routes
    {
      path: '/login',
      name: 'login',
      meta: {
        allowUnverified: true,
        guard: async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
          // If already logged in, redirect to home
          const usersStore = useUsersStore()
          if (usersStore.isLoggedIn) {
            return next({
              path: '/'
            })
          }
          return true
        }
      },
      component: () => import('../views/auth/LoginView.vue'),
    },
    {
      path: '/logout',
      name: 'logout',
      component: () => import('../views/auth/LogoutView.vue'),
    },
    {
      path: '/register',
      name: 'register',
      meta: {
        allowUnverified: true,
        guard: async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
          const usersStore = useUsersStore()
          if (usersStore.isLoggedIn) {
            // Forward the invited ADDRESS as well as the id. The invitations
            // page needs it to tell "this invitation is for someone else"
            // apart from "you have no invitations at all" — dropping it here
            // is exactly why that distinction could not be drawn before
            // (MYAC-1230).
            const invitation = invitationLinkQuery(to.query)
            if (invitation) {
              return next({ name: 'setup-invitations', query: invitation })
            }
            return next({ path: '/' })
          }
          return true
        }
      },
      component: () => import('../views/auth/RegisterView.vue'),
    },
    {
      path: '/forgotpassword',
      name: 'resetPassword',
      meta: { allowUnverified: true },
      component: () => import('../views/auth/ResetPassword.vue'),
    },
    {
      path: '/verify-email',
      name: 'verify-email',
      meta: { requiresAuth: true, allowUnverified: true },
      component: () => import('../views/auth/VerifyEmailView.vue'),
    },
    {
      // Re-consent interstitial (MYAC-824): shown when the in-force terms version
      // is newer (materially) than what the user last accepted.
      path: '/accept-terms',
      name: 'accept-terms',
      meta: { requiresAuth: true, allowUnverified: true },
      component: () => import('../views/auth/AcceptTermsView.vue'),
    },
    // Setup routes
    {
      path: '/setup',
      name: 'setup',
      meta: {
        requiresAuth: true,
        title: 'pageTitles.setup',
        guard: async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
          const tenantsStore = useTenantsStore()
          // Ensure tenants are loaded so we can inspect subscription status
          if (!tenantsStore.loadedOnce) {
            try { await tenantsStore.listTenants(false) } catch { }
          }
          if (tenantsStore.currentTenant?.subscription_data?.status === 'canceled' || tenantsStore.currentTenant?.subscription_data?.status === 'cancelled') {
            return next({ name: 'setup-resubscribe' })
          }
          return true
        },
      },
      component: () => import('../views/setup/SetupTenantView.vue'),
    },
    {
      path: '/setup/choice',
      name: 'setup-choice',
      meta: { requiresAuth: true, title: 'pageTitles.setupChoice' },
      component: () => import('../views/setup/SetupChoiceView.vue'),
    },
    {
      path: '/choose-tenant',
      name: 'choose-tenant',
      meta: {
        requiresAuth: true,
        title: 'pageTitles.chooseTenant',
        guard: async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
          const tenantsStore = useTenantsStore()
          // Ensure the subscription is set up. Note: listTenants resolves once
          // the Firestore subscription is open, NOT after the first snapshot
          // — we still need to wait for `loadedOnce` to flip.
          if (!tenantsStore.tenantsUnsub) {
            try { await tenantsStore.listTenants(false) } catch { }
          }
          // Wait briefly for the first snapshot. If it never lands, fall through
          // and let the view render in its loading state.
          if (!tenantsStore.loadedOnce) {
            const start = Date.now()
            while (!tenantsStore.loadedOnce && Date.now() - start < 3000) {
              await new Promise((r) => setTimeout(r, 50))
            }
          }
          // We can only redirect once we know the tenant count for sure.
          if (tenantsStore.loadedOnce) {
            const count = tenantsStore.tenants?.length || 0
            if (count === 0) return next({ name: 'setup-choice' })
            if (count === 1) return next({ name: 'dashboard' })
          }
          return true
        },
      },
      component: () => import('../views/setup/ChooseTenantView.vue'),
    },
    {
      path: '/setup/resubscribe',
      name: 'setup-resubscribe',
      meta: { requiresAuth: true, title: 'pageTitles.setupResubscribe' },
      component: () => import('@/views/setup/ResubscribeView.vue'),
    },
    // setup-project route removed; project creation handled via dialog
    {
      path: '/setup/invitations',
      name: 'setup-invitations',
      meta: { requiresAuth: true, title: 'pageTitles.setupInvitations' },
      component: () => import('../views/setup/SetupInvitationsView.vue'),
    },
    // Error routes
    {
      path: '/denied',
      name: 'denied',
      component: () => import('../views/errors/DeniedView.vue'),
    },
    {
      path: '/404',
      name: '404',
      component: () => import('../views/errors/404View.vue'),
    },
  ],
})

// MYAC-1522 — the translation layer's second half: a `?tab=` on a Pilotage
// section's own address (old internal pushes, a pasted link) becomes the clean
// address of that section, the other parameters kept.
router.beforeEach((to) => pilotageTabGuard(to))
// MYAC-1522 3b (drift D11) — each Pilotage section keeps its filters, per
// project, whichever way the member comes back to it.
installPilotageSectionFilters(router)

/**
 * MYAC-1522 — where a person with no company lands when they arrive from an
 * invitation link (`?invitation_id=…`, kept on `/` by the register screen): the
 * invitations page, carrying the link, when that invitation is pending for the
 * signed-in account. The page then accepts it on their behalf. `null` sends them
 * the ordinary way (setup-choice): a cancelled or foreign invitation must not
 * strand them on an empty invitations page.
 */
async function invitationLanding(to: RouteLocationNormalized): Promise<RouteLocationRaw | null> {
  const invitation = invitationLinkQuery(to.query)
  if (!invitation) return null
  const usersStore = useUsersStore()
  await usersStore.loadInvitationsForUser()
  const pending = (usersStore.invitations || []).some((inv) => inv.id === invitation.invitation_id)
  return pending ? { name: 'setup-invitations', query: invitation } : null
}

router.beforeEach(async (to, from, next) => {
  // Check maintenance mode first
  const maintenanceStore = useMaintenanceStore()

  // Check current maintenance status
  await maintenanceStore.checkMaintenanceStatus()

  // Check for bypass token in query params
  if (to.query.bypass) {
    maintenanceStore.setBypassToken(to.query.bypass as string)
  }

  // List of routes that are always accessible (legal pages and maintenance page itself)
  const exemptRoutes = ['maintenance', 'terms', 'privacy']

  // If maintenance is enabled and no bypass token, redirect to maintenance page
  if (maintenanceStore.isMaintenanceEnabled && !maintenanceStore.bypassToken && !exemptRoutes.includes(to.name as string)) {
    return next({
      path: '/maintenance',
    })
  }

  // Get current user
  const usersStore = useUsersStore()
  if (!usersStore.user) {
    const currentUser = await getCurrentUser()
    if (currentUser) {
      await usersStore.setUser(currentUser)
    }
  }

  // Load system config when user is authenticated
  if (usersStore.isLoggedIn) {
    const appStore = useAppStore()
    await appStore.loadConfig()
  }

  // Re-consent gate (MYAC-824): force acceptance of the in-force terms version
  // when a material update was published since the user last accepted. Mirrors
  // the no-tenant gate below; degrades permissively if the pointer can't be read.
  try {
    if (usersStore.isLoggedIn) {
      const appStore = useAppStore()
      await appStore.loadPublicConfig()
      const termsExempt = new Set(['accept-terms', 'logout', 'login', 'register', 'verify-email', 'maintenance', 'terms', 'privacy', 'denied', '404'])
      if (
        !termsExempt.has(String(to.name)) &&
        requiresConsent(appStore.publicConfig?.terms, usersStore.latestConsentSequence)
      ) {
        return next({ name: 'accept-terms', query: { redirect: to.fullPath } })
      }
    }
  } catch { }

  // Redirect to login if user is not authenticated
  if (to.meta.requiresAuth && !usersStore.isLoggedIn) {
    return next({
      path: '/login',
      query: { redirect: to.fullPath },
    })
  }

  // Redirect to denied if user is not super admin
  if (to.meta.requiresSuperAdmin && !usersStore.isSuperAdmin) {
    return next({
      path: '/denied',
    })
  }

  // Redirect authenticated users without any tenant to setup choice
  try {
    if (usersStore.isLoggedIn) {
      const tenantsStore = useTenantsStore()
      if (!tenantsStore.loadedOnce) {
        try { await tenantsStore.listTenants(false) } catch { }
      }
      const exempt = new Set(['setup-choice', 'setup', 'setup-invitations', 'choose-tenant', 'logout', 'login', 'register', 'verify-email', 'maintenance', 'terms', 'privacy', 'denied', '404'])
      if (tenantsStore.loadedOnce && (tenantsStore.tenants?.length || 0) === 0 && !exempt.has(String(to.name))) {
        return next(await invitationLanding(to) ?? { name: 'setup-choice' })
      }
    }
  } catch { }

  // Redirect to 404 if route is not found
  if (to.matched.length === 0) {
    return next({
      path: '/404',
    })
  }

  // call meta guard for each route
  for (const route of to.matched) {
    if (route.meta.guard && typeof route.meta.guard === 'function') {
      try {
        const result = await route.meta.guard(to, from, next)
        if (result !== true) {
          // The guard already called next() with its own redirect / abort.
          return result
        }
      } catch (error) {
        // A guard that threw/rejected (e.g. a forbidden or missing resource
        // load) must land on a clean page — not abort navigation (stuck on the
        // previous page) or blanket-404 an access denial (MYAC-758).
        logger.error('Meta guard error', error)
        return next({ name: accessErrorRouteName(error) })
      }
    }
  }

  return next()
})

// Handle chunk loading failures (e.g., when old chunks are deleted after deployment)
// Stale chunks reload (once per 30 s), anything else is logged (router/chunkReload.ts).
router.onError(onRouterError)

export default router

/**
 * MYAC-1348 — the current project is the project of the page on screen. A route
 * without a pid has none: clear it, or the dashboard, the tasks and the settings
 * keep reading the last project visited. Run after the navigation, so the page
 * being left keeps its project until it unmounts; project routes set theirs in
 * their guards. A route WITH a pid and no guard (the chat sandbox) is left alone.
 */
export function clearCurrentProjectOffProjectRoutes(to: Pick<RouteLocationNormalized, 'params'>): void {
  if (to.params.pid) return
  try { useProjectsStore().setCurrentProject('') } catch (error) { logger.error('AfterEach project reset', error) }
}

router.afterEach(clearCurrentProjectOffProjectRoutes)

// Track SPA page views after each navigation
router.afterEach((to) => {
  try {
    // Set page title
    const appStore = useAppStore()
    const titleKey = (to.meta.title as string | undefined) || null
    appStore.setTitle(titleKey)

    // Track analytics
    const analytics = useAnalyticsStore()
    const fullUrl = window.location.origin + router.resolve(to).fullPath
    const title = (to.name as string) ?? "unknown"
    // This silently no-ops if Analytics isn't initialized (e.g., no measurementId)
    analytics.trackPageView(fullUrl, title)

    // Activity tracking (MYAC-717): presence + a curated action when the route maps to one.
    // trackAction writes presence (bucket + last_seen_at) too. No-op if tracker not started.
    const routeName = typeof to.name === 'string' ? to.name : ''
    const mapped = ROUTE_ACTIONS[routeName]
    if (mapped) {
      const projectId = mapped.projectParam
        ? (to.params[mapped.projectParam] as string | undefined)
        : undefined
      trackAction(mapped.action, projectId ? { projectId, projectMeta: mapped.metaProvider?.() } : undefined)
    }
  } catch (error) {
    logger.error('AfterEach error', error)
  }
})
